<template>
  <draggable
    v-model="fileList"
    item-key="id"
    animation="200"
    :disabled="!props.reorder || hasUploadingFiles"
  >
    <template #item="{ element: file, index }">
      <div
        :key="`file-${file.id}-${index}`"
        class="my-4"
      >
        <div :class="['flex flex-col md:flex-row justify-between mb-4 gap-4', { 'drag-handle cursor-move': props.reorder && !hasUploadingFiles, 'cursor-not-allowed': hasUploadingFiles }]">
          <div
            class="w-full md:w-3/4 bg-faint-grey rounded-lg p-4"
            :ref="el => setFileRef(file.id, el)">
            <div class="w-full flex flex-col sm:flex-row sm:items-baseline">
              <div class="flex items-baseline mb-2 sm:mb-0">
                <DragHandleIcon
                  v-if="props.reorder"
                  :class="['mr-2 text-royal-blue-160 shrink-0 self-start', { 'opacity-10': hasUploadingFiles }]"
                  :is-decorative="true"/>
                <div
                  class="mr-4 shrink-0"
                  v-if="file.type === SubmissionFileType.Figure">
                  <div
                    class="w-auto min-w-6 h-6 flex items-center justify-center border-royal-blue-160 border-2 bg-white rounded-full text-base font-bold text-center px-2 leading-none"
                    aria-hidden="true">
                    Fig.&nbsp;{{ index + 1 }}
                  </div>
                </div>

                <div
                  class="mr-4 shrink-0"
                  v-if="file.type === SubmissionFileType.CrystalData || file.type === SubmissionFileType.Esi">
                  <div
                    class="w-6 h-6 flex-shrink-0 flex items-center justify-center border-royal-blue-160 border-2 bg-white rounded-full text-base font-bold text-center leading-none"
                    aria-hidden="true">
                    {{ index + 1 }}
                  </div>
                </div>
              </div>

              <div class="flex items-baseline flex-wrap min-w-0 flex-1">
                <strong
                  :id="`fileName${file.type}Id-${index}`"
                  class="data-hj-suppress mr-1 break-all"
                  :data-testid="`test-${file.type}Id-${index}`"
                  v-if="isExpanded(file.id)"
                  v-html="file.file"
                ></strong>

                <strong
                  v-else
                  :id="`fileName${file.type}Id-${index}`"
                  class="data-hj-suppress mr-1 break-all"
                  :data-testid="`test-${file.type}Id-${index}`"
                >
                  {{ truncateByCharacters(stripHtml(file.file), TRUNCATE_LIMIT) }}
                </strong>

                <ButtonComponent
                  v-if="shouldShowToggle(file.file)"
                  :label="isExpanded(file.id) ? 'View less' : 'View more'"
                  variant="tertiary"
                  class="ml-2"
                  @customClick="toggleFileContent(file.id)"
                  :data-testid="`toggle-das-content-${file.id}`"
                  :aria-expanded="isExpanded(file.id)"
                  :aria-controls="`fileName${file.type}Id-${index}`"
                />

                <span
                  :id="`fileSize${file.type}Id-${index}`"
                  v-if="(file.state === SubmissionFileState.Complete || file.state === SubmissionFileState.Uploading) && file.size"
                  class="font-roboto text-[#474747] whitespace-nowrap ml-1">
                  - {{ bytesFormatter(file.size) }}
                </span>
              </div>
              <div
                class="flex items-center justify-center w-[24px] h-[24px] ml-2 shrink-0 self-start"
                v-if="file.state === SubmissionFileState.Uploading || deletingFileId === file.id">
                <LoadingSpinner />
              </div>
            </div>
          </div>
          <div class="hidden md:invisible md:block">
            <span>or</span>
          </div>
          <div class="flex items-start gap-4">
            <slot
              name="editDAS"
              :disabled="file.state === SubmissionFileState.Uploading || file.state === SubmissionFileState.Failure || deletingFileId === file.id">
            </slot>
            <DeleteFileButton
              :id="`remove${file.type}FileId-${index}`"
              @delete="() => removeFile(file.id)"
              :disabled="file.state === SubmissionFileState.Uploading || deletingFileId === file.id"
              :file-type="file.type">
            </DeleteFileButton>
          </div>
        </div>
        <NotificationComponent
          v-if="failedDeleteFileIds.has(file.id)"
          headingText="Failed to delete file"
          :data-testid="`failed-to-delete-file-${file.type}`"
          variant="error"
          class="my-8">
          <template #body>
            <p>
              Unable to delete file. Please check your connection and try again. Contact
              <a
                :data-testid="`email-customer-service-delete-upload-${file.type}`"
                href="mailto:manuscriptsupport@rsc.org?subject=RSC%20Submissions%3A%20Unable%20to%20delete%20file"
                class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited">
                customer services</a> if the issue
              persists.
            </p>
          </template>
        </NotificationComponent>
      </div>
    </template>
  </draggable>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue'
import { SubmissionFileState, SubmissionFileType, ShowToastType, SubmissionFile } from '@/types'
import { useStore } from 'vuex'
import DeleteFileButton from '../upload/DeleteFileButton.vue'
import ButtonComponent from '../ui-components/ButtonComponent.vue'
import { bytesFormatter } from '../../services/formatters'
import LoadingSpinner from '../ui-components/LoadingSpinner.vue'
import NotificationComponent from '../ui-components/NotificationComponent.vue'
import { trackEvent } from '../../services/gtm'
import { AxiosError } from 'axios'
import draggable from 'vuedraggable'
import DragHandleIcon from '../ui-components/icons/DragHandleIcon.vue'
import { STANDARD_DAS_TEXT } from '@/constants/das'
import stripHtml from '@/utils/stripHtml'
import truncateByCharacters from '@/utils/truncateByCharacters'

const store = useStore()
const failedDeleteFileIds = ref<Set<string>>(new Set())
const fileContainerRefs = ref<Record<string, HTMLElement | null>>({})

const props = defineProps({
  fileListType: { required: true, type: String },
  reorder: { type: Boolean, default: false },
})

const showToast = inject('showToast') as (options: ShowToastType) => void

const deletingFileId = ref<string | null>(null)

// Computed property to get the file group
const expandedFiles = ref<Record<string, boolean>>({})

function setFileRef(fileId: string, el: HTMLElement | null | Element | unknown) {
  if (el instanceof HTMLElement) {
    fileContainerRefs.value![fileId] = el
  }
}

function toggleFileContent(fileId: string) {
  const isCurrentlyExpanded = !!expandedFiles.value[fileId]
  expandedFiles.value[fileId] = !isCurrentlyExpanded

  if (isCurrentlyExpanded) {
    const container = fileContainerRefs.value[fileId]
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}

function isExpanded(fileId: string) {
  return !!expandedFiles.value[fileId]
}

const TRUNCATE_LIMIT = 500

function shouldShowToggle(content: string) {
  return stripHtml(content).length > TRUNCATE_LIMIT
}

const fileGroup = computed(() => {
  if (!props.fileListType) return undefined
  const group = store.getters.groupedSubmissionFiles.find(g => g.type === props.fileListType)
  if (!group) return undefined
  if (group.type !== 'DAS') return group
  const files = (group.files || []).filter((f: SubmissionFile) => {
    return !(typeof f.file === 'string' && f.file.includes(STANDARD_DAS_TEXT))
  })
  if (files.length === 0) return undefined
  return { ...group, files }
})

// Check if any file in the list is currently uploading
const hasUploadingFiles = computed(() => {
  return fileList.value.some((file: SubmissionFile) => file.state === SubmissionFileState.Uploading)
})

const fileList = computed({
  get: (): SubmissionFile[] => fileGroup.value?.files || [],
  set: (newFiles: SubmissionFile[]) => {
    const newFilesIds = newFiles.map(item => item.id)
    const notNewFiles = store.state.activeSubmission.files.filter((item: SubmissionFile) => !newFilesIds.includes(item.id))
    const updatedGroups = [...notNewFiles, ...newFiles]

    const seenIds = new Set<string>()
    const uniqueFiles = updatedGroups.filter((file: SubmissionFile) => {
      if (seenIds.has(file.id)) return false
      seenIds.add(file.id)
      return true
    })

    store.commit('setGroupedSubmissionFiles', uniqueFiles)
  },
})

const removeFile = (fileId: string) => {
  deletingFileId.value = fileId
  failedDeleteFileIds.value.delete(fileId)
  store.dispatch('deleteFile', fileId)
    .then(() => {
      showToast({
        variant: 'success',
        headingText: 'Success',
        bodyContent: 'The file has been deleted successfully.',
        location: props.fileListType,
        isManual: false,
        isUpload: true,
      })
    })
    .catch((err) => {
      failedDeleteFileIds.value.add(fileId)
      trackDeleteUploadErrorEvent(err)
    })
    .finally(() => {
      deletingFileId.value = null
      if (props.fileListType === 'DAS') {
        store.commit('setDasCustomContent', '')
      }
    })
}

const trackDeleteUploadErrorEvent = (error: AxiosError | unknown) => {
  const status = (error as AxiosError)?.response?.status
  const message = (error as AxiosError)?.message
  trackEvent({
    category: 'Server Errors',
    action: 'error',
    event: `Delete${props.fileListType}FileError`,
    fileType: props.fileListType,
    fileId: deletingFileId.value || 'unknown',
    errorType: status ? `HTTP${status}` : 'UnknownError',
    errorDetails: message || 'No error message available',
    submissionId: store.state.activeSubmission.submissionId,
  })
}
</script>
